// ============================================================================
// ecgImageQuality.ts — УМНАЯ СЪЁМКА ЭКГ: оценка снимка ДО отправки в сервис.
// Считается на canvas: резкость (дисперсия отклика лапласиана по серому),
// средняя яркость, доля пересветов (яркость > 245) и заполнение кадра
// (доля не-белого содержимого). Оценка НЕ блокирует отправку — она даёт
// предупреждение и подсказку, решение остаётся за пользователем.
// ============================================================================

export interface EcgCaptureAssessment {
    /** Дисперсия отклика лапласиана (чем больше — тем резче снимок). */
    sharpness: number;
    /** Нормализованная резкость 0..1 (для строки качества). */
    sharpnessScore: number;
    /** Средняя яркость 0..255. */
    brightness: number;
    /** Доля пересвеченных пикселей (яркость > 245). */
    overexposedRatio: number;
    /** Доля не-белого содержимого (яркость < 240). */
    contentRatio: number;
    /** Понятные причины «плохого» кадра (пусто — кадр приемлем). */
    issues: string[];
    acceptable: boolean;
}

/** Подсказка при плохом качестве снимка (единая формулировка). */
export const ECG_CAPTURE_HINT = 'Держите телефон ровно, весь лист в кадре, без бликов и тени.';

const MIN_SHARPNESS = 80;
const SHARPNESS_SCORE_SCALE = 400;
const MIN_BRIGHTNESS = 70;
const OVEREXPOSED_LUMA = 245;
const MAX_OVEREXPOSED_RATIO = 0.1;
const CONTENT_LUMA = 240;
const MIN_CONTENT_RATIO = 0.5;
const ANALYSIS_MAX_SIDE = 480;
const MIN_IMAGE_SIDE = 8;

/**
 * analyzeEcgImage — оценить фотографию ЭКГ до отправки.
 * Ошибка чтения изображения — явное исключение (вызывающий показывает текст).
 */
export async function analyzeEcgImage(file: File): Promise<EcgCaptureAssessment> {
    const bitmap = await createImageBitmap(file);
    try {
        const scale = Math.min(1, ANALYSIS_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
        const width = Math.max(1, Math.round(bitmap.width * scale));
        const height = Math.max(1, Math.round(bitmap.height * scale));
        if (width < MIN_IMAGE_SIDE || height < MIN_IMAGE_SIDE) {
            throw new Error('Изображение слишком маленькое для оценки качества');
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) throw new Error('Браузер не предоставил canvas для оценки снимка');
        context.drawImage(bitmap, 0, 0, width, height);
        const image = context.getImageData(0, 0, width, height);
        return assessPixels(image.data, width, height);
    } finally {
        bitmap.close();
    }
}

function assessPixels(data: Uint8ClampedArray, width: number, height: number): EcgCaptureAssessment {
    const total = width * height;
    const gray = new Float32Array(total);
    let brightnessSum = 0;
    let overexposed = 0;
    let content = 0;

    for (let i = 0, pixel = 0; i < data.length; i += 4, pixel++) {
        const luma = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
        gray[pixel] = luma;
        brightnessSum += luma;
        if (luma > OVEREXPOSED_LUMA) overexposed++;
        if (luma < CONTENT_LUMA) content++;
    }

    let lapSum = 0;
    let lapSquareSum = 0;
    let lapCount = 0;
    for (let y = 1; y < height - 1; y++) {
        for (let x = 1; x < width - 1; x++) {
            const index = y * width + x;
            const lap = gray[index - width] + gray[index + width] + gray[index - 1] + gray[index + 1] - 4 * gray[index];
            lapSum += lap;
            lapSquareSum += lap * lap;
            lapCount++;
        }
    }

    const lapMean = lapCount > 0 ? lapSum / lapCount : 0;
    const sharpness = lapCount > 0 ? Math.max(0, lapSquareSum / lapCount - lapMean * lapMean) : 0;
    const sharpnessScore = Math.min(1, sharpness / SHARPNESS_SCORE_SCALE);
    const brightness = brightnessSum / total;
    const overexposedRatio = overexposed / total;
    const contentRatio = content / total;

    const issues: string[] = [];
    if (sharpness < MIN_SHARPNESS) issues.push('снимок нерезкий — телефон должен быть неподвижен');
    if (brightness < MIN_BRIGHTNESS) issues.push('слишком тёмно — включите свет');
    if (overexposedRatio > MAX_OVEREXPOSED_RATIO) issues.push('пересветы (блики) — уберите яркий свет');
    if (contentRatio < MIN_CONTENT_RATIO) issues.push('лист занимает меньше половины кадра — сфотографируйте его целиком');

    return {
        sharpness,
        sharpnessScore,
        brightness,
        overexposedRatio,
        contentRatio,
        issues,
        acceptable: issues.length === 0,
    };
}
